import { useMutation, useQuery } from 'react-query';
import { request, request_v2 } from '../utils/axios-utils';

const GetOrderListMinimal = async selectedCompany => {
    const { data } = await request_v2({
        method: 'get',
        url: '/order/orderListMinimal',
        params: {
            companyCode: selectedCompany,
        },
    });
    return data?.data;
};

export const useGetOrderListMinimal = selectedCompany =>
    useQuery(['orderListMinimal', selectedCompany], () => GetOrderListMinimal(selectedCompany), {
        refetchOnWindowFocus: false,
    });

export const fetchAllOrders = data => {
    const [_, pageNum, pageSize, companyCode, isCSA = false, orderName] = data?.queryKey;
    const nameQuery = companyCode && orderName ? `&orderName=${encodeURIComponent(orderName)}` : '';
    return request({
        url: `/order?pageNum=${pageNum}&pageSize=${pageSize}&companyCode=${companyCode}&isCSA=${isCSA}${nameQuery}`,
        method: 'GET',
    });
};

export const fetchNotificationsForOrder = data => {
    const [_, orderId] = data?.queryKey;
    if (!orderId) {
        return null;
    }
    const result = request({
        url: `/order/getNotifications/${orderId}`,
        method: 'GET',
    });
    return result;
};

export const fetchAllNotifications = data => {
    const [_, companyCode] = data?.queryKey;
    const params = {};
    if (companyCode) {
        params.companyCode = companyCode;
    }
    return request({
        url: '/order/getAllNotifications',
        method: 'GET',
        params,
    });
};

export const rerunAllRejectedNotificationForClient = orderId => {
    const result = request({
        url: `/order/rerunAllRejectedNotificationForClient`,
        method: 'POST',
        data: { orderId },
    });
    return result;
};

const addOrder = orderData => {
    return request({ url: '/order', method: 'POST', data: orderData });
};

export const useAddOrder = (onSuccess, onError) => {
    return useMutation(addOrder, {
        onSuccess: onSuccess,
        onError: onError,
    });
};

export const fetchOrderDetails = orderId => {
    return request({ url: `/order/${orderId}`, method: 'GET' });
};

const createShipmentData = data => {
    return request({
        url: '/order/createShipment',
        method: 'POST',
        data: data,
    });
};
const searchCSASku = params => {
    return request({
        url: `csa/search-sku`,
        method: 'get',
        params,
    });
};
const csaTaggingList = params => {
    return request({
        url: `csa/tagging-dropdown`,
        method: 'get',
        params,
    });
};
const getCSAList = payload => {
    return request({
        url: `csa/orders`,
        method: 'post',
        data: payload,
    });
};

const getCSAFilters = payload => {
    return request({
        url: `csa/csa-filters`,
        method: 'post',
        data: payload,
    });
};
const getAllCarts = (pageNum, pageSize, cartStage, displayName) => {
    return request({
        url: `csa/cart-list`,
        method: 'get',
        params: {
            pageNum,
            pageSize,
            cartStage: cartStage || undefined,
            displayName: displayName || undefined,
        },
    });
};
export const useCsaTaggingList = (params, onSuccess, onError) => {
    return useQuery(['csaTaggingList', params], () => csaTaggingList(params), {
        onSuccess,
        onError,
        refetchOnWindowFocus: false,
        select: data => data?.data,
        enabled: !!(params && params.companyCode),
    });
};
export const useSearchCSASku = (params, onSuccess, onError) => {
    return useQuery(['searchCSASku', params], () => searchCSASku(params), {
        onSuccess,
        onError,
        refetchOnWindowFocus: false,
        select: data => data?.data,
        enabled: !!(params && params.sku),
    });
};
export const useGetCSAList = (payload, onSuccess, onError) => {
    return useQuery(['csaList', payload], () => getCSAList(payload), {
        onSuccess: onSuccess,
        onError: onError,
        refetchOnWindowFocus: false,
        select: data => data?.data,
        enabled: !!payload,
    });
};
export const useGetCSAFilters = (payload, onSuccess, onError) => {
    return useQuery(['csaFilters', payload], () => getCSAFilters(payload), {
        onSuccess: onSuccess,
        onError: onError,
        refetchOnWindowFocus: false,
        enabled: !!(payload && Array.isArray(payload.osids) && payload.osids.length),
    });
};
export const useGetAllCarts = (pageNum, pageSize, cartStage, displayName, onSuccess, onError) => {
    return useQuery(['csaCartList', pageNum, pageSize, cartStage, displayName], () => getAllCarts(pageNum, pageSize, cartStage, displayName), {
        onSuccess: onSuccess,
        onError: onError,
        refetchOnWindowFocus: false,
        select: data => data?.data,
    });
};

export const getCartDetails = cartId => {
    return request({
        url: `csa/cart/${cartId}`,
        method: 'get',
    });
};

const markOrderCSA = payload => {
    return request({
        url: `/order/markOrderAsClientSupportingArticle/${payload.orderId}`,
        method: 'PATCH',
        data: {
            isClientSupportingArticle: payload.isClientSupportingArticle,
        },
    });
};

export const useMarkOrderCSA = (onSuccess, onError) => {
    return useMutation(markOrderCSA, {
        onSuccess: onSuccess,
        onError: onError,
    });
};

const syncDataToAIStyling = payload => {
    return request({
        url: `/order/syncDataToAIStyling`,
        method: 'PATCH',
        data: {
            orderId: payload.orderId,
        },
    });
};

export const useSyncDataToAIStyling = (onSuccess, onError) => {
    return useMutation(syncDataToAIStyling, {
        onSuccess: onSuccess,
        onError: onError,
    });
};

export const useCreateShipment = (onSuccess, onError) => {
    return useMutation(createShipmentData, {
        onSuccess: onSuccess,
        onError: onError,
        // onError: (err)=> err.response.data,
    });
};

const bookOrderData = data => {
    return request({
        url: '/order/book',
        method: 'POST',
        data: data,
    });
};

export const useBookOrder = (onSuccess, onError) => {
    return useMutation(bookOrderData, {
        onSuccess: onSuccess,
        onError: onError,
    });
};

const downloadPII = data => {
    return request({
        url: '/order/downloadPII',
        method: 'POST',
        data: data,
    });
};
export const useDownloadPII = (onSuccess, onError) => {
    return useMutation(downloadPII, {
        onSuccess: onSuccess,
        onError: onError,
    });
};

const downloadCC = data => {
    return request({
        url: '/order/downloadCCImages',
        method: 'POST',
        data: data,
    });
};
export const useDownloadCC = (onSuccess, onError) => {
    return useMutation(downloadCC, {
        onSuccess: onSuccess,
        onError: onError,
    });
};

const bookReturnShipment = data => {
    return request({
        url: '/order/bookReturnShipment',
        method: 'POST',
        data: data,
    });
};

export const useBookReturnShipment = (onSuccess, onError) => {
    return useMutation(bookReturnShipment, {
        onSuccess: onSuccess,
        onError: onError,
    });
};

const returnShipmentData = data => {
    return request({
        url: '/order/createReturnShipment',
        method: 'POST',
        data: data,
    });
};

export const useReturnShipment = (onSuccess, onError) => {
    return useMutation(returnShipmentData, {
        onSuccess: onSuccess,
        onError: onError,
    });
};

const deleteShipmentData = data => {
    return request({
        url: '/order/deleteShipment',
        method: 'POST',
        data: data,
    });
};

export const useDeleteShipment = (onSuccess, onError) => {
    return useMutation(deleteShipmentData, {
        onSuccess: onSuccess,
        onError: onError,
    });
};
const getSlotList = data => {
    return request({
        url: `/order/shipment/listAvailablePickupSlots?courierId=${data.queryKey[0]}`,
        method: 'GET',
    });
};
export const useGetSlots = (onSuccess, onError, qId) => {
    return useQuery(qId, getSlotList, {
        onSuccess: onSuccess ? onSuccess : null,
        onError: onError,
        enabled: false,
        refetchOnWindowFocus: false,
        // select:  data.data,
    });
};

export const piRequestSuccess = data => {
    const [_, orderId, trackNumber] = data?.queryKey;

    return request({
        url: `/order/reopenPII/approve?orderId=${orderId}&trackingNumber=${trackNumber}`,
        method: 'PUT',
    });
};

export const piRequestReject = data => {
    const [_, orderId, trackNumber] = data?.queryKey;

    return request({
        url: `/order/reopenPII/reject?orderId=${orderId}&trackingNumber=${trackNumber}`,
        method: 'PUT',
    });
};

const generateInboundReport = data => {
    return request({
        url: '/order/inboundReport',
        method: 'POST',
        data: data,
    });
};
export const useGenerateInboundReport = (onSuccess, onError) => {
    return useMutation(generateInboundReport, {
        onSuccess: onSuccess,
        onError: onError,
    });
};

export const useGetAllBoxesOfOrder = (orderId, onSuccess, onError) =>
    useQuery(['orderBoxes', orderId], () => fetchOrderDetails(orderId), {
        refetchOnWindowFocus: false,
        enabled: !!orderId,
        onSuccess: onSuccess,
        onError: onError,
    });

export const fetchOrderPII = (pageNum, pageSize, searchName) => {
    console.log(pageNum, pageSize, searchName);
    return request({ url: `/inboundDrafts/list`, method: 'GET', params: { pageNum, pageSize, searchName } });
};

export const useGetAllPIIOfOrder = (pageNum, pageSize, searchName, onSuccess, onError) =>
    useQuery(['orderPII', pageNum, pageSize, searchName], () => fetchOrderPII(pageNum, pageSize, searchName), {
        refetchOnWindowFocus: true,
        onSuccess: onSuccess,
        onError: onError,
    });

const processInboundDraft = data => {
    return request({
        url: '/inboundDrafts/process',
        method: 'POST',
        data: data,
    });
};

export const useProcessInboundDraft = (onSuccess, onError) => {
    return useMutation(processInboundDraft, {
        onSuccess: onSuccess,
        onError: onError,
    });
};

const GetFxBarcodeDetails = async orderId => {
    if (!!orderId) {
        return await request_v2({
            method: 'GET',
            url: `/order/getFxBarcodeDetails?orderId=${orderId}`,
        });
    } else {
        return null;
    }
};

const UpdateFxBarcodeGeneratedCount = async (orderId, count, barcodes) => {
    const order = await request_v2({
        method: 'POST',
        url: '/order/updateFxBarcodeGeneratedCount',
        body: {
            orderId,
            count,
            barcodes,
        },
    });
    return order;
};

export const useUpdateFxBarcodeGeneratedCount = () =>
    useMutation({
        mutationFn: async ([orderId, count, barcodes]) => {
            return await UpdateFxBarcodeGeneratedCount(orderId, count, barcodes);
        },
    });

export const useGetFxBarcodeDetails = orderId => useQuery(['FxBarcodeDetails', orderId], () => GetFxBarcodeDetails(orderId));

export const getPresignedUrlForPIIUpload = payload => {
    return request({ url: `articles/preSignedPathForPIIUpload`, method: 'GET', params: payload });
};

const addOtherImages = payload => {
    return request({
        url: `csa/append-other-images`,
        method: 'post',
        data: payload,
    });
};
export const useAddOtherImages = (onSuccess, onError) => {
    return useMutation(addOtherImages, {
        onSuccess: onSuccess,
        onError: onError,
    });
};
const retagArticle = ({ articleId, tagData }) => {
    return request({
        url: `csa/retag-article/${articleId}`,
        method: 'put',
        data: tagData,
    });
};

export const useRetagArticle = (onSuccess, onError) => {
    return useMutation(retagArticle, {
        onSuccess: onSuccess,
        onError: onError,
    });
};
